import {
  ClipboardCheck,
  Fan,
  Flag,
  Sun,
  Truck,
  Warehouse,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

/**
 * Les métiers : ce qu'un partenaire sait faire, et ce que demande chaque étape
 * de la chaîne. Depuis S10, la chaîne est libre (src/shared/referentiel/chantier.ts) ;
 * les métiers, eux, restent fixes — ils relient les étapes aux partenaires, au
 * planning et aux zones.
 */
export const METIERS = [
  /* La première étape de chaque chantier : le relevé sur place (db/019-visite-technique.sql). */
  { id: 'visite', libelle: 'Visite technique', court: 'Visite', icone: ClipboardCheck },
  {
    id: 'livraison',
    libelle: 'Livraison et réception de matériel',
    court: 'Livraison',
    icone: Truck,
  },
  { id: 'solaire', libelle: 'Installation solaire', court: 'Solaire', icone: Sun },
  { id: 'batiment', libelle: 'Aménagement du bâtiment', court: 'Bâtiment', icone: Warehouse },
  { id: 'sechage', libelle: 'Système de séchage', court: 'Séchage', icone: Fan },
  {
    id: 'reception',
    libelle: 'Réception du chantier — visite finale',
    court: 'Réception',
    icone: Flag,
  },
  { id: 'autre', libelle: 'Autre intervention', court: 'Autre', icone: Wrench },
] as const satisfies readonly { id: string; libelle: string; court: string; icone: LucideIcon }[];

export type Metier = (typeof METIERS)[number]['id'];

/** Une teinte par métier, pour reconnaître une étape d'un coup d'œil dans l'agenda. */
export interface Teinte {
  /** Fond clair, texte foncé et liseré d'une carte. */
  carte: string;
  /** Une pastille pleine : légende, points du mois. */
  point: string;
}

export const TEINTE_METIER: Record<Metier, Teinte> = {
  visite: { carte: 'border-violet-500 bg-violet-50 text-violet-900', point: 'bg-violet-500' },
  livraison: { carte: 'border-sky-500 bg-sky-50 text-sky-900', point: 'bg-sky-500' },
  solaire: { carte: 'border-amber-500 bg-amber-50 text-amber-900', point: 'bg-amber-500' },
  batiment: { carte: 'border-stone-500 bg-stone-100 text-stone-900', point: 'bg-stone-500' },
  sechage: { carte: 'border-teal-500 bg-teal-50 text-teal-900', point: 'bg-teal-500' },
  reception: {
    carte: 'border-emerald-600 bg-emerald-50 text-emerald-900',
    point: 'bg-emerald-600',
  },
  autre: { carte: 'border-slate-400 bg-slate-50 text-slate-900', point: 'bg-slate-400' },
};

/** Le SAV n'est pas une étape de la chaîne : sa propre teinte. */
export const TEINTE_SAV: Teinte = {
  carte: 'border-rose-500 bg-rose-50 text-rose-900',
  point: 'bg-rose-500',
};

/** Les métiers qu'un partenaire déclare : « autre » ne se déclare pas. */
export const METIERS_PARTENAIRE = METIERS.filter((m) => m.id !== 'autre');

export const IDS_METIERS: readonly Metier[] = METIERS.map((m) => m.id);

export function estMetier(valeur: string): valeur is Metier {
  return (IDS_METIERS as readonly string[]).includes(valeur);
}

export function metier(id: string) {
  return METIERS.find((m) => m.id === id);
}
